/**
 * 按帧时间自动降级（design §6「降级」、§10 第 10 条）：手机掉帧、发烫时一档一档往下降，顺畅了再试着升回来。
 *
 * - 每 2 秒取一次这段时间帧间隔的中位数：连着两次超过 24 毫秒（约 42 帧以下）就降一档。
 * - 降过以后连着 20 秒顺畅（中位数不到 18 毫秒）就试着升回一档；升上去 4 秒内又慢了就退回来，这次打开不再往上试。
 * - 连着降两档帧时间都没变好（没快一成），说明慢不是因为画得多（比如 iPhone 省电模式把帧率锁在 30），
 *   就退回降之前那档，这次打开不再自动降。
 * - 刚开始的 3 秒、每次换档后的 2 秒不算（编译着色器、重新分配缓冲会卡一下）；切到后台回来那种超过 250 毫秒的帧不算。
 * 档位按设备记住（main.ts），下次打开从记住的那档开始。
 */

export interface QualityLevel {
  /** 像素比上限 */
  dpr: number;
  /** 全屏流体：full 原样；lite 低分辨率、只在交互后 2 秒内算；off 关掉 */
  fluid: 'full' | 'lite' | 'off';
  bloom: boolean;
  /** 装饰粒子（背景天幕）画多少 */
  dust: number;
  /** 最多同时显影多少张照片 */
  photos: number;
}

/** 从好到差；顺序就是降级的顺序：先降像素比，再降流体，再减星点和照片，最后关辉光、关流体 */
export const QUALITY_LEVELS: readonly QualityLevel[] = [
  { dpr: 2, fluid: 'full', bloom: true, dust: 1, photos: 1024 },
  { dpr: 1.5, fluid: 'full', bloom: true, dust: 1, photos: 1024 },
  { dpr: 1.5, fluid: 'lite', bloom: true, dust: 1, photos: 1024 },
  { dpr: 1.5, fluid: 'lite', bloom: true, dust: 0.5, photos: 384 },
  { dpr: 1, fluid: 'lite', bloom: true, dust: 0.5, photos: 384 },
  { dpr: 1, fluid: 'lite', bloom: false, dust: 0.5, photos: 256 },
  { dpr: 1, fluid: 'off', bloom: false, dust: 0.35, photos: 256 },
];

const WINDOW = 2000;
const SLOW = 24;
const GOOD = 18;
const GOOD_FOR = 20_000;
const SETTLE_START = 3000;
const SETTLE_CHANGE = 2000;
const MAX_DT = 250;

const median = (a: number[]) => {
  const s = a.slice().sort((x, y) => x - y);
  return s[s.length >> 1]!;
};

export class QualityGovernor {
  level: number;
  private readonly fixed: boolean;
  private readonly max = QUALITY_LEVELS.length - 1;
  /** 最好能升到哪一档（升级试过失败后就不再往上） */
  private best = 0;
  /** 不再自动降（降了没用） */
  private noDown = false;
  private samples: number[] = [];
  private windowStart = -1;
  private settleUntil: number;
  private slow = 0;
  private goodFor = 0;
  /** 最近几次降档：降之前的档位和那时的中位数，用来判断降了有没有用 */
  private downs: { from: number; median: number }[] = [];
  /** 刚升过一档：升之前的档位和截止时间 */
  private probe: { from: number; until: number } | null = null;

  constructor(opts: { start: number; fixed: boolean; now: number }) {
    this.level = Math.min(this.max, Math.max(0, Math.round(opts.start)));
    this.fixed = opts.fixed;
    this.settleUntil = opts.now + SETTLE_START;
  }

  /** 每帧调一次：dt 是和上一帧的间隔（毫秒）。档位变了返回新的档位，没变返回 null */
  sample(dt: number, now: number): number | null {
    if (this.fixed || dt > MAX_DT || now < this.settleUntil) return null;
    if (this.windowStart < 0) this.windowStart = now;
    this.samples.push(dt);
    if (now - this.windowStart < WINDOW) return null;
    const m = median(this.samples), span = now - this.windowStart;
    this.samples = [];
    this.windowStart = now;
    return this.judge(m, span, now);
  }

  private set(level: number, now: number): number {
    this.level = level;
    this.slow = 0;
    this.goodFor = 0;
    this.samples = [];
    this.windowStart = -1;
    this.settleUntil = now + SETTLE_CHANGE;
    return level;
  }

  private judge(m: number, span: number, now: number): number | null {
    // 刚升过一档：又慢了就退回来，以后不再往上试
    if (this.probe) {
      if (m > SLOW) {
        const back = this.probe.from;
        this.probe = null;
        this.best = back;
        return this.set(back, now);
      }
      if (now >= this.probe.until) this.probe = null;
    }
    // 刚降过档：看降了有没有用
    const last = this.downs.at(-1);
    if (last && last.median > 0) {
      if (m <= last.median * 0.9) this.downs = [];
      else if (this.downs.length >= 2) {
        // 连着降两档都没快一成：慢不是因为画得多，退回最开始那档、不再自动降
        const back = this.downs[0]!.from;
        this.downs = [];
        this.noDown = true;
        return this.set(back, now);
      } else last.median = -1;
    }
    if (m > SLOW) {
      this.goodFor = 0;
      if (++this.slow >= 2 && this.level < this.max && !this.noDown) {
        this.downs.push({ from: this.level, median: m });
        return this.set(this.level + 1, now);
      }
      return null;
    }
    this.slow = 0;
    if (m < GOOD) {
      this.goodFor += span;
      if (this.goodFor >= GOOD_FOR && this.level > this.best) {
        this.probe = { from: this.level, until: now + 4000 + SETTLE_CHANGE };
        this.downs = [];
        return this.set(this.level - 1, now);
      }
    } else this.goodFor = 0;
    return null;
  }
}
